import { createRoot } from "react-dom/client";
import { initSentry, enableSentryReplayOnConsent } from "./lib/sentry";
import { initPostHog } from "./lib/posthog";
import { initMetaPixel, captureFbclid } from "./lib/meta-pixel";
import { initGoogleAds } from "./lib/google-ads";
import { initTikTokPixel } from "./lib/tiktok-pixel";
import { i18nReady } from "./i18n/config";
import App from "./App.tsx";
import "./index.css";
// Package Scanner v2 brand fonts: Geist (display) + Caveat (the seal signature).
// Inter Tight (body) and JetBrains Mono (mono) load via the existing stack.
import "@fontsource/geist-sans/400.css";
import "@fontsource/geist-sans/500.css";
import "@fontsource/geist-sans/600.css";
import "@fontsource/geist-sans/700.css";
import "@fontsource-variable/caveat";
import { attemptChunkReload } from "./lib/reloadGuard";

// Initialize Sentry before React renders so early errors are captured.
initSentry();

// Recover from "stale chunk" errors that surface after a deploy while a
// long-lived tab still references the old index-*.js hash. The dynamic
// import() rejects, Suspense surfaces the failure, and the user sees a
// blank route until they hard-refresh. We one-shot reload instead, gated
// by sessionStorage so a genuinely broken chunk can't loop forever.
if (typeof window !== "undefined") {
  const handleStaleChunk = (event: Event) => {
    event.preventDefault();
    // attemptChunkReload() self-guards against held work (a running scan), a
    // backgrounded/hidden tab, and an offline blip (all of which routinely drop
    // an in-flight dynamic import where a reload would discard the user's work),
    // plus the reload-loop cooldown. It no-ops in those cases. Genuine
    // post-deploy stale ROUTE chunks on a visible, online tab still recover.
    attemptChunkReload();
  };
  window.addEventListener("vite:preloadError", handleStaleChunk);
  window.addEventListener("unhandledrejection", (e) => {
    const reason = e.reason as { message?: string; name?: string } | undefined;
    const msg = reason?.message ?? "";
    if (
      /Failed to fetch dynamically imported module/i.test(msg) ||
      /Importing a module script failed/i.test(msg)
    ) {
      handleStaleChunk(e);
    }
  });
}

// Defer non-essential trackers (PostHog + Meta Pixel + Google Ads) until the
// main thread is idle so their fetches don't compete with LCP assets on the
// landing page. All initializers are consent-gated (Quebec Law 25 / PIPEDA): on
// a first visit they no-op because no analytics consent exists yet. The
// cr:consent listener starts them the moment the visitor accepts in
// CookieConsent, so a late opt-in still gets analytics without a page reload.
// The Meta Pixel used to load eagerly from index.html before any consent — that
// gap (QA F11) is closed by routing it through the same consent gate here; the
// Google Ads gtag.js tag is loaded the same way for the same reason. 2026-06-04.
if (typeof window !== "undefined") {
  // Capture the Meta ad-click id (`fbclid`) synchronously on first paint —
  // BEFORE the deferred, consent-gated pixel loads and before any client-side
  // route change strips it from the URL. This only stashes a first-party value
  // (no Meta hit, no `_fbc` cookie yet), so it is safe pre-consent; initMetaPixel
  // materializes `_fbc` from it once the visitor accepts. Without this, sign-ups
  // reached the pixel with no ad-click id and never attributed to the campaign.
  captureFbclid();

  const startTrackers = () => {
    initPostHog();
    initMetaPixel();
    initGoogleAds();
    // TikTok used to load eagerly from index.html and set its advertising
    // cookies pre-consent (found 2026-08-01) — now gated here like the rest.
    initTikTokPixel();
  };
  if ("requestIdleCallback" in window) {
    window.requestIdleCallback(startTrackers, { timeout: 3000 });
  } else {
    setTimeout(startTrackers, 2000);
  }
  window.addEventListener("cr:consent", (e: Event) => {
    const choice = (e as CustomEvent<string>).detail;
    if (choice === "accepted") {
      startTrackers();
      // Sentry itself is already running (essential error monitoring); a late
      // opt-in only turns on the non-essential Session Replay piece.
      enableSentryReplayOnConsent();
    }
  });
}

// Clean up old Supabase project token (one-time migration)
try {
  const oldKey = 'sb-pwsinghvwcepdvqqmnvj-auth-token';
  if (localStorage.getItem(oldKey)) {
    localStorage.removeItem(oldKey);
  }
} catch {}

// Only `en` ships in the entry chunk; the other five locales load on demand (see
// i18n/config.ts). Wait for the detected language before the first render so a
// French/Spanish/Punjabi/Hindi/Urdu visitor does not get a flash of English.
// English visitors resolve on the spot — no request, no delay. i18nReady never
// rejects, and `finally` means even a failed locale fetch still mounts the app on
// the en fallback rather than leaving a blank page.
void i18nReady.finally(() => {
  createRoot(document.getElementById("root")!).render(<App />);
});
